import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useClientes from "../../hooks/useClientes";
import useAtendimento from "../../hooks/useAtendimento";
import useAgendarAtendimento from "../../hooks/useAgendarAtendimento";
import { useAuth } from "../../auth/AuthContext";
import ModalAgendarAtendimento from "../../components/recepcao/ModalAgendarAtendimento";
import { formatarNomeUpper, aplicarMascaraCPF, aplicarMascaraTelefone} from "../../utils/formatadores"

function validarCPF(cpfCliente) {
  cpfCliente = cpfCliente.replace(/\D/g, "");

  if (cpfCliente.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpfCliente)) return false;

  let soma = 0;
  let resto;

  for (let i = 1; i <= 9; i++) {
    soma += parseInt(cpfCliente.substring(i - 1, i)) * (11 - i);
  }

  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpfCliente.substring(9, 10))) return false;

  soma = 0;

  for (let i = 1; i <= 10; i++) {
    soma += parseInt(cpfCliente.substring(i - 1, i)) * (12 - i);
  }

  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpfCliente.substring(10, 11))) return false;

  return true;
}

// 📅 Função para verificar se o agendamento é hoje ou data passada
const eHojeOuAnterior = (dataAgendamentoStr) => {
  if (!dataAgendamentoStr) return false;

  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, "0");
  const dia = String(hoje.getDate()).padStart(2, "0");
  const hojeFormatado = `${ano}-${mes}-${dia}`;

  return dataAgendamentoStr <= hojeFormatado;
};

const Recepcao = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const { buscarCliente } = useClientes();
  const { fila, checkinCliente, cancelarAtendimento } = useAtendimento();

  // 🗓️ HOOK DE AGENDAMENTOS
  const {
    proximosAgendamentos,
    buscarProximosAgendamentos,
    atualizar: atualizarAgendamento,
    excluir: excluirAgendamento,
    loading: loadingProximos
  } = useAgendarAtendimento();

  const [busca, setBusca] = useState("");
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [actionType, setActionType] = useState(null);

  // 🗓️ ESTADOS DO MODAL E DROPDOWN
  const [modalAgendarAberto, setModalAgendarAberto] = useState(false);
  const [mostrarProximosAgendamentos, setMostrarProximosAgendamentos] = useState(false);

  useEffect(() => {
    buscarProximosAgendamentos(7);
  }, [buscarProximosAgendamentos]);

  if (loading) return <p>Carregando...</p>;
  if (!user) return <p>Não autorizado</p>;
  if (
    user.role !== "admin" &&
    user.role !== "advogado" &&
    user.role !== "advogado_parceiro" &&
    user.role !== "estagiario" &&
    user.role !== "recepcionista"
  ) {
    return <p>Acesso negado</p>;
  }

  const handleSubmitBuscar = (e) => {
    e.preventDefault();
    handleBuscar();
  };

  const handleBuscar = async () => {
    const somenteNumeros = busca.replace(/\D/g, "");

    if (somenteNumeros.length === 11 && !validarCPF(busca)) {
      alert("CPF inválido");
      return;
    }
    setActionType("buscar");
    try {
      const resultados = await buscarCliente(busca);

      setResultadosBusca(resultados);
      setClienteEncontrado(null);
      setBuscaRealizada(true);
    } catch (err) {
      console.error(err);
      alert("Erro ao buscar cliente");
    } finally {
      setActionType(null);
    }
  };

  const liberarAtendimento = async () => {
    if (!clienteEncontrado) return;

    const jaEstaNaFila = (fila || []).some((item) => {
      const clienteIdNaFila = item.cliente?._id || item.cliente;
      return (
        clienteIdNaFila === clienteEncontrado._id &&
        item.status === "aguardando"
      );
    });

    if (jaEstaNaFila) {
      alert("Este cliente já está na fila de atendimento!");
      return;
    }

    setActionType("liberarAtendimento");
    try {
      await checkinCliente({ _id: clienteEncontrado._id });

      alert("Cliente liberado para atendimento");

      setClienteEncontrado(null);
      setBusca("");
      setResultadosBusca([]);
      setBuscaRealizada(false);
    } catch (err) {
      console.error(err);
      alert("Erro ao liberar cliente para atendimento");
    } finally {
      setActionType(null);
    }
  };

  // 🚀 LIBERAR CLIENTE AGENDADO PARA A FILA DE ATENDIMENTO
  const handleLiberarAgendamento = async (agendamento) => {
    const clienteObj = agendamento.cliente;
    const clienteId = clienteObj?._id || clienteObj;

    if (!clienteId) {
      alert("Dados do cliente não encontrados no agendamento.");
      return;
    }

    // Verifica se já está na fila aguardando
    const jaEstaNaFila = (fila || []).some((item) => {
      const idFila = item.cliente?._id || item.cliente;
      return idFila === clienteId && item.status === "aguardando";
    });

    if (jaEstaNaFila) {
      alert("Este cliente já está na fila de atendimento!");
      return;
    }

    setActionType(`liberar-${agendamento._id}`);
    try {
      // 1. Faz o check-in (adiciona à fila de espera)
      await checkinCliente({ _id: clienteId });

      // 2. Atualiza o status diretamente para 'concluido' no backend
      await atualizarAgendamento(agendamento._id, { status: "concluido" });

      alert("Cliente liberado para atendimento com sucesso!");

      // 3. Recarrega a lista dos próximos agendamentos (irá remover este da visualização)
      await buscarProximosAgendamentos(7);
    } catch (err) {
      console.error(err);
      alert(err.message || "Erro ao liberar atendimento do cliente.");
    } finally {
      setActionType(null);
    }
  };

  const handleFecharModalAgendar = () => {
    setModalAgendarAberto(false);
    buscarProximosAgendamentos(7);
  };

  // 🗑️ EXCLUSÃO DE AGENDAMENTO
  const handleExcluirAgendamento = async (id) => {
    const confirmar = window.confirm("Deseja realmente excluir este agendamento?");
    if (!confirmar) return;

    try {
      await excluirAgendamento(id);
      alert("Agendamento excluído com sucesso!");
      buscarProximosAgendamentos(7);
    } catch (err) {
      console.error(err);
      alert(err.message || "Erro ao excluir agendamento.");
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h1>RECEPÇÃO</h1>

        {/* 🔍 BUSCA E AÇÕES NA MESMA LINHA */}
        <form
          className="search-inline"
          onSubmit={handleSubmitBuscar}
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            flexWrap: "nowrap",
            margin: "0 auto 20px auto"
          }}
        >
          <input
            placeholder="CPF ou Nome"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ width: "260px", margin: 0 }}
          />
          <button
            type="submit"
            disabled={actionType !== null}
            style={{ margin: 0, whiteSpace: "nowrap" }}
          >
            {actionType === "buscar" ? (
              <span className="spinner-loader"></span>
            ) : (
              "Buscar"
            )}
          </button>
          <button
            type="button"
            className="botao-primary"
            onClick={() => setModalAgendarAberto(true)}
            disabled={actionType !== null}
            style={{ margin: 0, whiteSpace: "nowrap" }}
          >
            Agendar atendimento
          </button>
        </form>

        {/* 🔎 RESULTADOS DA BUSCA */}
        {resultadosBusca.length > 0 && (
          <div className="card" style={{ marginTop: "15px" }}>
            <h3>Clientes encontrados:</h3>

            {resultadosBusca.map((c) => (
              <div
                className="action-buttons"
                key={c._id}
                style={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <span>
                  {formatarNomeUpper(c.nomeCliente)} - {aplicarMascaraCPF(c.cpf)}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setClienteEncontrado(c);
                    setResultadosBusca([]);
                  }}
                  disabled={loading}
                >
                  {loading ? "Carregando..." : "Selecionar"}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ⚠️ MENSAGEM SE NENHUM CLIENTE FOR ENCONTRADO */}
        {buscaRealizada && resultadosBusca.length === 0 && !clienteEncontrado && (
          <div className="card" style={{ marginTop: "15px", textAlign: "center" }}>
            <p>Nenhum cliente encontrado para "{busca}".</p>
            <button
              type="button"
              onClick={() => navigate("/cadastro-cliente")}
              style={{ marginTop: "10px" }}
            >
              Ir para Cadastro de Cliente
            </button>
          </div>
        )}

        {/* 👤 CLIENTE SELECIONADO */}
        {clienteEncontrado && (
          <div className="card" style={{ marginTop: "15px" }}>
            <p><b>Nome:</b> {formatarNomeUpper(clienteEncontrado.nomeCliente)}</p>
            <p><b>CPF:</b> {aplicarMascaraCPF(clienteEncontrado.cpf)}</p>
            <p><b>Telefone:</b> {aplicarMascaraTelefone(clienteEncontrado.telefone)}</p>
            <p><b>Email:</b> {clienteEncontrado.email}</p>

            <button
              type="button"
              onClick={liberarAtendimento}
              disabled={actionType !== null}
            >
              {actionType === "liberarAtendimento" ? (
                <span className="spinner-loader"></span>
              ) : (
                "Liberar para atendimento"
              )}
            </button>
          </div>
        )}

        <hr />

        <h2>Aguardando Atendimento</h2>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Horário</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {(fila || [])
                .filter((c) => c && c.status === "aguardando")
                .sort((a, b) => {
                  const dataA = a?.horarioCheckin ? new Date(a.horarioCheckin) : new Date(0);
                  const dataB = b?.horarioCheckin ? new Date(b.horarioCheckin) : new Date(0);
                  return dataA - dataB;
                })
                .map((item) => (
                  <tr key={item._id}>
                    <td>
                      {item.cliente?.nomeCliente ||
                        item.cliente?.nome ||
                        "Cliente sem nome"}
                    </td>
                    <td>
                      {item.horarioCheckin
                        ? new Date(item.horarioCheckin).toLocaleString()
                        : "Horário não informado"}
                    </td>
                    <td>{item.status}</td>
                    <td>
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmar = window.confirm(
                            "Deseja cancelar este atendimento?"
                          );

                          if (!confirmar) return;

                          await cancelarAtendimento(item._id);
                        }}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* 📅 DROPDOWN: AGENDAMENTOS DOS PRÓXIMOS 7 DIAS */}
        <div style={{ marginTop: "25px" }}>
          <button
            type="button"
            className="botao-primary"
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 15px",
              cursor: "pointer"
            }}
            onClick={() =>
              setMostrarProximosAgendamentos(!mostrarProximosAgendamentos)
            }
          >
            <span>
              📅 Agendamentos dos Próximos 7 Dias ({proximosAgendamentos.length})
            </span>
            <span>{mostrarProximosAgendamentos ? "▲" : "▼"}</span>
          </button>

          {mostrarProximosAgendamentos && (
            <div className="table-container" style={{ marginTop: "15px" }}>
              {loadingProximos ? (
                <p style={{ textAlign: "center", padding: "15px" }}>
                  Carregando agendamentos...
                </p>
              ) : proximosAgendamentos.length === 0 ? (
                <p style={{ textAlign: "center", padding: "15px" }}>
                  Nenhum agendamento para os próximos 7 dias.
                </p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Hora</th>
                      <th>Cliente</th>
                      <th>CPF</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proximosAgendamentos.map((item) => {
                      const podeLiberar = eHojeOuAnterior(item.dataAgendamento);
                      const isLiberando = actionType === `liberar-${item._id}`;

                      return (
                        <tr key={item._id}>
                          <td>
                            {item.dataAgendamento
                              ? new Date(
                                  `${item.dataAgendamento}T00:00:00`
                                ).toLocaleDateString("pt-BR")
                              : "N/A"}
                          </td>
                          <td>{item.horaAgendamento || "--:--"}</td>
                          <td>
                            {item.cliente?.nomeCliente ||
                              item.cliente?.nome ||
                              item.nomeCliente ||
                              "Cliente sem nome"}
                          </td>
                          <td>{item.cliente?.cpf || item.cpf || "N/A"}</td>
                          <td>{item.status}</td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            {podeLiberar && (
                              <button
                                type="button"
                                className="botao-sucesso"
                                style={{ marginRight: "8px" }}
                                onClick={() => handleLiberarAgendamento(item)}
                                disabled={actionType !== null}
                              >
                                {isLiberando ? (
                                  <span className="spinner-loader"></span>
                                ) : (
                                  "Liberar para atendimento"
                                )}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleExcluirAgendamento(item._id)}
                              disabled={actionType !== null || loadingProximos}
                            >
                              Excluir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 🗓️ RENDERIZAÇÃO DO MODAL DE AGENDAMENTO */}
      {modalAgendarAberto && (
        <ModalAgendarAtendimento onClose={handleFecharModalAgendar} />
      )}
    </div>
  );
};

export default Recepcao;